import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLinkedin, FaTiktok, FaFacebook, FaYoutube, FaWhatsapp } from 'react-icons/fa';

function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      alert(`Thank you! We will keep you updated at ${email}`);
      setEmail('');
    }
  };

  const socialLinks = [
    {
      href: 'https://www.linkedin.com/company/not-ashamed-of-the-gospel-ministry/',
      Icon: FaLinkedin,
      label: 'LinkedIn',
    },
    {
      href: 'https://www.tiktok.com/@naogm09',
      Icon: FaTiktok,
      label: 'TikTok',
    },
    {
      href: 'https://shorturl.at/cImrY',
      Icon: FaFacebook,
      label: 'Facebook',
    },
    {
      href: 'http://www.youtube.com/@NAOGM21',
      Icon: FaYoutube,
      label: 'YouTube',
    },
    {
      href: 'https://shorturl.at/9uoko',
      Icon: FaWhatsapp,
      label: 'WhatsApp',
    },
  ];

  return (
    <footer className="bg-primary text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">

          {/* Brand + Description */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-5">
              <img
                src="/assets/images/logo.png"
                alt="NAOGM Logo"
                className="h-11 w-auto"
                onError={(e) =>
                  (e.currentTarget.src = 'https://via.placeholder.com/120x40?text=NAOGM')
                }
              />
              <span className="font-bold text-xl tracking-wide">NAOGM</span>
            </Link>

            <p className="text-white/80 text-sm leading-relaxed mb-6">
              Not Ashamed of the Gospel Ministry spreading the message of hope, 
              faith, and transformation across nations.
            </p>

            {/* Social Icons */}
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 flex items-center justify-center rounded-full 
                             bg-white/10 hover:bg-accent text-white transition-all 
                             duration-300 hover:scale-110"
                >
                  <social.Icon className="w-4.5 h-4.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h5 className="text-lg font-bold mb-5">
              {t('footer.linksTitle')}
            </h5>
            <ul className="space-y-3">
              {[
                { to: '/about', label: t('header.about') },
                { to: '/ministries', label: t('header.ministries') },
                { to: '/events', label: t('header.events') },
                { to: '/testimonials', label: t('header.testimonials') },
                { to: '/prayer-request', label: t('header.prayerRequest') },
                { to: '/donate', label: t('header.donate') },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-white/80 hover:text-accent transition-colors duration-200 text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h5 className="text-lg font-bold mb-5">
              {t('footer.aboutTitle')}
            </h5>
            <div className="space-y-4 text-sm text-white/80">
              <p>
                <span className="block text-white font-medium mb-1">
                  {t('footer.headquartersLabel')}
                </span>
                {t('contact.address')}
              </p>
              <p>
                <span className="block text-white font-medium mb-1">
                  {t('footer.emailLabel')}
                </span>
                <a
                  href="mailto:info@notashamedofthegospelministry.org"
                  className="hover:text-accent transition-colors"
                >
                  info@notashamedofthegospelministry.org
                </a>
              </p>
              <p>
                <span className="block text-white font-medium mb-1">
                  {t('footer.phoneLabel')}
                </span>
                <a
                  href="tel:+243857274262"
                  className="hover:text-accent transition-colors"
                >
                  +243 857 274 262
                </a>
              </p>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h5 className="text-lg font-bold mb-5">
              Stay Connected
            </h5>
            <p className="text-white/80 text-sm mb-5">
              Receive updates about crusades, events, and testimonies.
            </p>

            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 
                           text-white placeholder-white/50 text-sm focus:outline-none 
                           focus:ring-2 focus:ring-accent focus:border-transparent transition"
              />
              <button
                type="submit"
                className="w-full bg-accent hover:bg-yellow-500 text-white font-semibold 
                           py-3 rounded-lg transition-all duration-300 hover:scale-[1.02]"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
          <p>
            © {currentYear} <span className="text-white">NAOGM</span>. {t('footer.rightsReserved')}
          </p>
          
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-accent transition-colors">
              Privacy Policy
            </Link>
            <span className="text-white/30">|</span>
            <Link to="/contact" className="hover:text-accent transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;